<div class="page-layout">
  <app-toast></app-toast>

  <app-sidebar></app-sidebar>

  <div class="main-area">
    <app-navbar
      pageTitle="Knowledge Graph"
      pageSubtitle="Visualize relationships extracted from ingested documents."
    ></app-navbar>

    <div class="content">
      <app-pending-reviews-banner></app-pending-reviews-banner>
      <div class="graph-card">
        <div class="card-header">
          <div class="header-left">
            <h3 class="card-title">Generated Graph</h3>
          </div>

          <div class="header-right">
            <label for="documentSelect">Document</label>
            <select id="documentSelect" [value]="selectedDocument()" (change)="onDocumentChange($event)">
              <option value="">All Documents</option>
              <option *ngFor="let doc of documents(); trackBy: trackByDocumentName" [value]="doc.name">
                {{ doc.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Canvas is ALWAYS in the DOM so Cytoscape never detaches -->
        <div class="graph-body">
          <!-- Loading overlay (on top of canvas) -->
          <div class="loading-overlay" *ngIf="isLoadingGraph()">
            <div class="spinner"></div>
          </div>

          <!-- Empty state overlay -->
          <div class="empty-overlay" *ngIf="!isLoadingGraph() && nodeCount() === 0">
            <app-empty-state
              title="No data yet"
              message="There are no relationships for the selected scope."
            ></app-empty-state>
          </div>

          <div class="graph-stage">
            <div #graphCanvas class="graph-canvas"></div>

            <!-- Context menu -->
            <div class="context-menu" *ngIf="contextMenu().visible"
                 [style.left.px]="contextMenu().x" [style.top.px]="contextMenu().y">
              <button type="button" class="ctx-item" (click)="onContextExpand()">
                <span class="ctx-icon">🔍</span> Show Neighbors
              </button>
              <button type="button" class="ctx-item" (click)="onContextFocus()">
                <span class="ctx-icon">🎯</span> Focus Node
              </button>
              <button type="button" class="ctx-item" (click)="onContextCopy()">
                <span class="ctx-icon">📋</span> Copy ID
              </button>
            </div>

            <!-- Legend -->
            <div class="legend">
              <div class="legend-item">
                <span class="legend-dot entity"></span> Entity
              </div>
              <div class="legend-item">
                <span class="legend-dot document"></span> Document
              </div>
              <div class="legend-item">
                <span class="legend-line"></span> Relationship
              </div>
            </div>

            <!-- Layout controls -->
            <div class="layout-controls">
              <span class="layout-label">Layout</span>
              <button type="button" class="layout-btn"
                      *ngFor="let layout of availableLayouts"
                      [class.active]="currentLayout() === layout"
                      (click)="applyLayout(layout)">
                {{ layout }}
              </button>
            </div>

            <div class="graph-toolbar">
              <span class="graph-stats">
                <ng-container *ngIf="totalNodeCount() as total; else plainCount">
                  {{ nodeCount() }} of {{ total }} nodes (most connected) · {{ edgeCount() }} edges
                </ng-container>
                <ng-template #plainCount>{{ nodeCount() }} nodes · {{ edgeCount() }} edges</ng-template>
                <span class="streaming-dot" *ngIf="isStreamingMore()" title="Loading more…"></span>
              </span>
              <button type="button" class="toolbar-btn" *ngIf="canLoadMore()" [disabled]="isLoadingMore()" (click)="loadMore()">
                {{ isLoadingMore() ? 'Loading…' : 'Load More' }}
              </button>
              <button type="button" class="toolbar-btn" (click)="resetView()">Reset View</button>
            </div>
          </div>

          <div class="side-column">
              <!-- Ask the graph: same search as the Graph Playground, run over the nodes drawn here -->
              <div class="ask-card" [class.collapsed]="!askOpen()">
                <div class="ask-head">
                  <span class="ask-title">Ask the graph</span>
                  <button type="button" class="ask-toggle" (click)="askOpen.set(!askOpen())"
                          [attr.aria-label]="askOpen() ? 'Collapse' : 'Expand'">{{ askOpen() ? '−' : '+' }}</button>
                </div>

                <div class="ask-body" *ngIf="askOpen()">
                  <div class="ask-input-row">
                    <input type="text"
                           [ngModel]="question()"
                           (ngModelChange)="question.set($event)"
                           placeholder="Name something on the graph…"
                           [disabled]="askBusy() || nodeCount() === 0"
                           (keydown.enter)="askGraph()" />
                    <button type="button" class="ask-go" (click)="askGraph()"
                            [disabled]="askBusy() || !question().trim() || nodeCount() === 0">
                      {{ askBusy() ? '…' : 'Ask' }}
                    </button>
                  </div>

                  <ng-container *ngIf="askStatus() !== 'idle'">
                    <div class="ask-status">
                      <span *ngIf="askStatus() === 'searching'">Walking the graph…</span>
                      <span *ngIf="askStatus() === 'answering'">Found {{ askNodeTotal() }} nodes, {{ askEdgeTotal() }} links. Writing the answer…</span>
                      <span *ngIf="askStatus() === 'done' && askNodeTotal() > 0">Used {{ askNodeTotal() }} nodes and {{ askEdgeTotal() }} links</span>
                      <span *ngIf="askStatus() === 'done' && askNodeTotal() === 0">No match</span>
                      <button type="button" class="ask-back" *ngIf="askLifted()" (click)="releaseFocus()">Put nodes back</button>
                      <button type="button" class="ask-clear" (click)="clearAsk()" title="Clear">✕</button>
                    </div>

                    <div class="ask-block" *ngIf="askSeeds().length">
                      <span class="ask-label">Starts from</span>
                      <div class="ask-seeds">
                        <span class="ask-seed" *ngFor="let s of askSeeds()">{{ s }}</span>
                      </div>
                    </div>

                    <div class="ask-block" *ngIf="askTrace().length">
                      <span class="ask-label">Path followed</span>
                      <ul class="ask-trace">
                        <li *ngFor="let t of askTrace()" [style.padding-left.px]="t.depth * 10">
                          <span class="hop">{{ t.depth }}</span>
                          {{ t.from }} <em>{{ t.relationship }}</em> {{ t.to }}
                        </li>
                      </ul>
                    </div>

                    <div class="ask-note" *ngIf="askIsLocalPreview()">No answer service connected. Showing what the search found.</div>
                    <div class="ask-answer" *ngIf="askAnswer()">{{ askAnswer() }}</div>
                  </ng-container>

                  <div class="ask-hint" *ngIf="askStatus() === 'idle'">
                    Searches the {{ nodeCount() }} nodes shown here. Matching nodes light up, then the search spreads along their links.
                  </div>
                </div>
              </div>
            <aside class="details-panel" [class.hidden]="!selectedType()">
              <ng-container *ngIf="selectedType(); else detailsHintTpl">
                <span class="detail-kind">{{ selectedType() === 'node' ? 'Node' : 'Edge' }}</span>
                <h4 class="detail-title">{{ selectedTitle() }}</h4>
                <p class="detail-subtitle">{{ selectedSubtitle() }}</p>

                <div class="detail-row" *ngFor="let item of selectedMeta()">
                  <span class="detail-label">{{ item.label }}</span>
                  <span class="detail-value">{{ item.value }}</span>
                </div>
              </ng-container>
            </aside>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ng-template #detailsHintTpl>
    <div class="details-hint">
      Click a node or edge to inspect details. Right-click a node for actions.
    </div>
  </ng-template>
</div>
